import React from "react";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useSelector } from "react-redux";
import { createMaterial, editMaterial } from "../../redux/actions/material";
import { store } from "../../store";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import { showSuccessAlert } from "../ui/utils/AlertUtils";
import { getProtocols } from "../../services/protocolService";
import { useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";

export const ModalMaterial = ({
  visible,
  onClose,
  studyId,
  studyCode,
  materialId = null,
  initialValues = {},
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(materialId);
  const [name, setName] = React.useState("");
  const [type, setType] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [unitPrice, setUnitPrice] = React.useState("");
  const isLoading = useSelector((state) => state.material.isLoadingMaterial);
  const created = useSelector((state) => state.material.createdMaterial);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [protocols, setProtocols] = React.useState([]);
  const [selectedProtocolIds, setSelectedProtocolIds] = React.useState([]);

  // Prefill fields when opening (edit mode if materialId provided)
  React.useEffect(() => {
    if (visible) {
      setName(initialValues.name || "");
      setType(initialValues.type || "");
      setStatus(initialValues.status || "");
      setUnitPrice(
        initialValues.unitPrice !== undefined &&
          initialValues.unitPrice !== null
          ? String(initialValues.unitPrice)
          : "",
      );
      setSelectedProtocolIds(initialValues.protocolIds || []);
      setErrorMessage("");
    }
  }, [visible, initialValues, materialId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (name && type && status && selectedProtocolIds.length > 0) {
      setName("");
      setType("");
      setStatus("");
      setUnitPrice("");
      if (isEdit) {
        // Update existing material (PUT)
        store
          .dispatch(
            editMaterial(materialId, {
              name,
              type,
              status,
              unitPrice: unitPrice === "" ? null : Number(unitPrice),
              protocolIds: selectedProtocolIds,
            }),
          )
          .then(() => onClose(true));
      } else {
        // Create new material (POST)
        store
          .dispatch(
            createMaterial({
              name,
              type,
              status,
              unitPrice: unitPrice === "" ? null : Number(unitPrice),
              studyId,
              protocolIds: selectedProtocolIds,
            }),
          )
          .then(() => onClose(true));
      }
    } else {
      setErrorMessage(t("pleaseFillAllRequiredFields"));
    }
  };

  React.useEffect(() => {
    if (created) {
      showSuccessAlert(t("materialCreatedSuccess"));
    }
  }, [created, t]);

  React.useEffect(() => {
    const fetchProtocols = async () => {
      const protocols = await getProtocols(studyId);
      setProtocols(protocols);
    };

    fetchProtocols();
  }, [studyId]);

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    setErrorMessage("");
  };

  return (
    <Modal open={visible}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
        component="form"
        noValidate
        onSubmit={handleSubmit}>
        <IconButton
          style={{
            position: "absolute",
            top: 10,
            right: 10,
          }}
          onClick={() => {
            onClose(false);
          }}>
          <CloseIcon></CloseIcon>
        </IconButton>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" component="h2">
              {isEdit ? t("update") : t("createMaterial")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
              {t("pleaseIndicateFields")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                required
                name="name"
                value={name}
                onChange={handleInputChange(setName)}
                id="input-name"
                label={t("name")}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="type-label">{t("type")}*</InputLabel>
              <Select
                labelId="type-label"
                label={t("type")}
                id="type"
                name="type"
                value={type}
                onChange={handleInputChange(setType)}
                required>
                <MenuItem value={"material"}>{t("kit")}</MenuItem>
                <MenuItem value={"supply"}>{t("supply")}</MenuItem>
                <MenuItem value={"reagent"}>{t("reagent")}</MenuItem>
                <MenuItem value={"document"}>{t("document")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="status-label">{t("status")}*</InputLabel>
              <Select
                labelId="status-label"
                label={t("status")}
                id="status"
                name="status"
                value={status}
                onChange={handleInputChange(setStatus)}
                required>
                <MenuItem value={"active"}>{t("active")}</MenuItem>
                <MenuItem value={"planned"}>{t("planned")}</MenuItem>
                <MenuItem value={"finished"}>{t("finished")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                name="unitPrice"
                value={unitPrice}
                onChange={handleInputChange(setUnitPrice)}
                id="input-unit-price"
                label={t("unitPrice")}
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
              />
            </FormControl>
          </Grid>

          {/* ------------------------------------------------------- */}
          {/* Protocols Multiple Select */}
          {/* ------------------------------------------------------- */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="select-protocol">{t("protocols")}*</InputLabel>
              <Select
                labelId="select-protocol"
                id="select-protocol"
                multiple
                value={selectedProtocolIds}
                onChange={(e) => {
                  setSelectedProtocolIds(e.target.value);
                  setErrorMessage("");
                }}
                name="select-protocol"
                label={t("protocols")}
                input={<OutlinedInput label={t("protocols")} />}
                renderValue={(selected) =>
                  protocols
                    .filter((p) => selected.includes(p.id))
                    .map((p) => p.reference)
                    .join(", ")
                }>
                {protocols.map((protocol) => (
                  <MenuItem key={protocol.id} value={protocol.id}>
                    <Checkbox
                      checked={selectedProtocolIds.indexOf(protocol.id) > -1}
                    />
                    <span>{protocol.reference}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="study-label">{t("study")}</InputLabel>
              <Select
                labelId="study-label"
                id="study-select"
                value={studyCode}
                input={<OutlinedInput label={t("study")} />}
                disabled>
                <MenuItem value={studyCode}>{studyCode}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || !!errorMessage}
              sx={{ mt: 3, mb: 2, padding: 2 }}>
              {isEdit ? t("update") : t("create")}
            </Button>
          </Grid>
        </Grid>
        {errorMessage && (
          <p
            style={{
              color: "#721c24",
              backgroundColor: "#f8d7da",
              borderColor: "#f5c6cb",
              padding: ".75rem 1.25rem",
              marginBottom: "1rem",
              border: "1px solid transparent",
              borderRadius: ".25rem",
            }}>
            {errorMessage}
          </p>
        )}
      </Box>
    </Modal>
  );
};
